<script setup lang="ts">
import { useEventListener } from '@vueuse/core'

const { query } = useRoute()
const { locale } = useI18n()
const { getProduct } = useProducts()
const { addToBagAction } = useCartActions()
const { jewelleryBuilderEnabled } = useFeatureFlags()
const jewelleryBuilderUrl = ref('')

const messageEventHandler = async function (event: MessageEvent) {
  if (event.origin === jewelleryBuilderUrl.value) {
    if (event.data.type === 'addItemToCart') {
      const productId = event.data.productId
      const specialOrderSKU = event.data.specialOrderSKU
      const size = event.data.ringSize
      const selectedPcpOptionId = event.data.planCode
      const product = await getProduct(productId)
      const selectedPcpOption =
        product?.options
          ?.find((option) => option.id === 'pcp')
          ?.values?.find((value) => value.id === selectedPcpOptionId) || null

      if (!product) return

      // The PCP option is only sent when the shopper actually picked one.
      const pcpOptionParams = selectedPcpOptionId
        ? { optionItems: [{ optionId: 'pcp', optionValueId: selectedPcpOptionId }] }
        : {}

      addToBagAction(
        {
          productId,
          specialOrderSKU,
          size,
          ...pcpOptionParams,
          popup: {
            product,
            selectedPcpOption: selectedPcpOption
              ? {
                  id: selectedPcpOption?.id,
                  name: selectedPcpOption?.name || '',
                }
              : null,
          },
        },
        {
          product: product,
          locale: locale.value,
          location: 'product detail page',
        },
      )
    }

    if (event.data.type === 'toggleMenu') {
      if (event.data.showMenu) {
        document.querySelectorAll('.header, .header-border').forEach((el) => el.classList.remove('header--hidden'))
      } else {
        document.querySelectorAll('.header, .header-border').forEach((el) => el.classList.add('header--hidden'))
      }
    }
  }
}

usePageContentType().setContentType('pdp')

onMounted(async () => {
  onMountedFirstLoadHandler() //REVIEW: Update GA tracking if we need to differentiate from regular PDP
  jewelleryBuilderUrl.value = useRuntimeConfig().public.jewelexConfiguratorUrl

  await nextTick() // Need to wait for the DOM to be updated before accessing the iframe

  const iframe = document.getElementById('jewellery-builder-iframe') as HTMLIFrameElement

  if (!iframe) {
    console.error('Iframe element not found after rendering')
    return
  }

  iframe.onload = function () {
    iframe.contentWindow?.postMessage(
      {
        origin: 'michaelhill',
        prodSku: query.sku,
      },
      jewelleryBuilderUrl.value,
    )
  }

  useEventListener('message', messageEventHandler)
})

useHead({
  title: 'Jewellery Builder | Michael Hill',
  meta: [
    {
      name: 'description',
      content: 'Customize and build your jewellery with our jewellery builder.',
    },
  ],
})
</script>

<template>
  <div v-if="jewelleryBuilderEnabled()" class="jewellery-builder">
    <iframe
      v-if="jewelleryBuilderUrl"
      id="jewellery-builder-iframe"
      :src="`${jewelleryBuilderUrl}/home/`"
      frameborder="0"
      width="100%"
      height="100%"
      allowfullscreen
    >
    </iframe>
  </div>
  <NotFoundPage v-else />
</template>

<style lang="postcss">
.jewellery-builder {
  width: 100%;
  height: 80vh;
  overflow: hidden;

  @media (--mobile-only) {
    height: 100vh;
  }
}

body:has(.header--hidden) {
  margin-top: var(--space-00);
}
</style>
